import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormatter, useTranslations } from "next-intl";
import { Field, Input } from "@/components/ui";
import { apiErrorText, readApiError, readApiErrorResponse } from "@/lib/api-error";
import { apiUrl } from "@/services/http/api-url";
import { platformFetch } from "@/services/http/platform-fetch";

type Kind = "stream" | "storage";

/** `GET …/storage-config/:kind` (Plan 4 S10): never a secret, only hints. */
type StorageConfigView = {
  configured: boolean;
  publicConfig: ({ hints: Record<string, string> } & Record<string, unknown>) | null;
  inUse: number;
  lastTestAt: string | null;
  lastTestOk: boolean | null;
  lastTestError: string | null;
};
type TestResult = { ok: boolean; message: string | null };

const SECRETS: Record<Kind, string[]> = {
  stream: ["apiKey", "readOnlyApiKey", "tokenKey"],
  storage: ["password", "tokenKey"],
};
const PLAIN: Record<Kind, string[]> = {
  stream: ["libraryId", "cdnHostname"],
  storage: ["zone", "region", "storageHost", "cdnHost"],
};
const IDENTITY: Record<Kind, string> = { stream: "libraryId", storage: "zone" };

const path = (tenantId: string, kind: Kind) =>
  `/platform/admin/tenants/${encodeURIComponent(tenantId)}/storage-config/${kind}`;
const queryKey = (tenantId: string, kind: Kind) => ["platform-storage-config", tenantId, kind];

/**
 * R4-F6 (MT-1-D213): the address Bunny posts to is on the API's origin. `base` is explicit
 * because `resolveApiBaseUrl()` is `/api` whenever Vite is in dev; the page origin is used only
 * when the base is relative (dev / same origin).
 */
export function bunnyWebhookUrl(
  tenantId: string,
  base?: string,
  origin: string = window.location.origin,
): string {
  const u = apiUrl(`/v1/webhooks/bunny-stream/${tenantId}`, base);
  return u.startsWith("/") ? origin + u : u;
}

function BunnyForm({ tenantId, kind }: { tenantId: string; kind: Kind }) {
  const t = useTranslations("platform.bunnyConfig");
  const tRoot = useTranslations();
  const format = useFormatter();
  const client = useQueryClient();
  const id = `bunny-${kind}`;
  const key = queryKey(tenantId, kind);
  const query = useQuery({
    queryKey: key,
    queryFn: async (): Promise<StorageConfigView> => {
      const res = await platformFetch(path(tenantId, kind));
      if (!res.ok) throw await readApiErrorResponse(res);
      return ((await res.json()) as { data: StorageConfigView }).data;
    },
  });
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"save" | "test" | "remove" | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<"copied" | "failed" | null>(null);
  const view = query.data;
  const saved = view?.publicConfig ?? null;

  const value = (field: string): string =>
    edits[field] ?? (SECRETS[kind].includes(field) ? "" : String(saved?.[field] ?? ""));

  function describeError(err: unknown, fallback: "saveFailed" | "testRequestFailed"): string {
    const base = apiErrorText(err, tRoot, `platform.bunnyConfig.${fallback}`, {
      namespace: "platform.errors",
    });
    const parsed = readApiError(err);
    const fields = parsed?.fields as unknown;
    if (parsed?.code === "VALIDATION_ERROR" && Array.isArray(fields)) {
      const lines = (fields as Array<{ path?: string; message?: string }>)
        .map((f) => `${f.path ?? ""}: ${f.message ?? ""}`)
        .join("; ");
      return `${base} ${lines}`.trim();
    }
    return base;
  }

  function strandMessage(): string {
    return t(kind === "stream" ? "inUseStream" : "inUseStorage", { count: view?.inUse ?? 0 });
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    const body: Record<string, unknown> = {};
    for (const field of [...PLAIN[kind], ...SECRETS[kind]]) {
      const v = value(field).trim();
      if (v !== "") body[field] = v;
    }
    const identity = IDENTITY[kind];
    const changesIdentity =
      view?.configured && saved && String(saved[identity] ?? "") !== String(body[identity] ?? "");
    if (changesIdentity && view.inUse > 0) {
      if (!window.confirm(strandMessage())) return;
      body.confirmReplace = true;
    }
    setBusy("save");
    try {
      const res = await platformFetch(path(tenantId, kind), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setEdits({});
      setMessage(t("saved"));
      void client.invalidateQueries({ queryKey: key });
      void client.invalidateQueries({ queryKey: ["platform-school", tenantId] });
    } catch (err) {
      setError(describeError(err, "saveFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function runTest() {
    setMessage(null);
    setError(null);
    setBusy("test");
    try {
      const res = await platformFetch(`${path(tenantId, kind)}/test`, { method: "POST" });
      if (!res.ok) throw await readApiErrorResponse(res);
      const result = ((await res.json()) as { data: TestResult }).data;
      setMessage(result.ok ? t("testOk") : t("testFailed", { message: result.message ?? "" }));
      void client.invalidateQueries({ queryKey: key });
      void client.invalidateQueries({ queryKey: ["platform-school", tenantId] });
    } catch (err) {
      setError(describeError(err, "testRequestFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    setMessage(null);
    setError(null);
    const body: Record<string, unknown> = {};
    const prompt =
      (view?.inUse ?? 0) > 0
        ? strandMessage()
        : t("removeConfirm", {
            what: t(kind === "stream" ? "removeWhatStream" : "removeWhatStorage"),
          });
    if (!window.confirm(prompt)) return;
    if ((view?.inUse ?? 0) > 0) body.confirmReplace = true;
    setBusy("remove");
    try {
      const res = await platformFetch(path(tenantId, kind), {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw await readApiErrorResponse(res);
      setEdits({});
      setMessage(t("removed"));
      void client.invalidateQueries({ queryKey: key });
      void client.invalidateQueries({ queryKey: ["platform-school", tenantId] });
    } catch (err) {
      setError(describeError(err, "saveFailed"));
    } finally {
      setBusy(null);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  const label = (field: string) => t(field as "libraryId");
  const hintFor = (field: string): string | undefined =>
    SECRETS[kind].includes(field) && view?.configured && saved?.hints?.[field]
      ? t("savedKey", { hint: saved.hints[field] })
      : undefined;
  const when = (iso: string) =>
    format.dateTime(new Date(iso), { dateStyle: "medium", timeStyle: "short" });

  let status: string;
  if (!view?.configured) status = t("notConfigured");
  else if (!view.lastTestAt) status = t("notTested");
  else if (view.lastTestOk) status = t("lastTestOk", { date: when(view.lastTestAt) });
  else
    status = t("lastTestFailed", { date: when(view.lastTestAt), error: view.lastTestError ?? "" });

  const webhook = kind === "stream" ? bunnyWebhookUrl(tenantId) : null;

  return (
    <section
      className="mt-4 rounded-md border border-[var(--border)] p-4"
      aria-labelledby={`${id}-title`}
      data-testid={`bunny-config-${kind}`}
    >
      <h3 id={`${id}-title`} className="text-base font-semibold">
        {t(kind === "stream" ? "streamTitle" : "storageTitle")}
      </h3>
      {query.isPending && <p className="mt-2 text-sm text-[var(--text-muted)]">{t("loading")}</p>}
      {query.isError && (
        <p role="alert" className="mt-2 text-sm text-[var(--danger)]">
          {apiErrorText(query.error, tRoot, "platform.bunnyConfig.loadFailed", {
            namespace: "platform.errors",
          })}
        </p>
      )}
      {view && (
        <>
          <p className="mt-2 text-sm text-[var(--text-muted)]" data-testid={`${id}-status`}>
            {status}
          </p>
          <form onSubmit={save} className="mt-3 flex flex-col gap-4">
            {[...PLAIN[kind], ...SECRETS[kind]].map((field) => {
              const secret = SECRETS[kind].includes(field);
              return (
                <Field key={field} label={label(field)} hint={hintFor(field)}>
                  <Input
                    type={secret ? "password" : "text"}
                    autoComplete={secret ? "off" : undefined}
                    spellCheck={secret ? false : undefined}
                    value={value(field)}
                    onChange={(e) => setEdits((prev) => ({ ...prev, [field]: e.target.value }))}
                  />
                </Field>
              );
            })}
            <p className="text-xs text-[var(--text-muted)]">{t("effectNote")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={busy !== null}
                className="h-10 rounded-md bg-[var(--ink-9)] px-4 text-sm font-medium text-[var(--text-inverse)] disabled:opacity-60"
              >
                {busy === "save" ? t("saving") : t("save")}
              </button>
              {view.configured && (
                <>
                  <button
                    type="button"
                    onClick={() => void runTest()}
                    disabled={busy !== null}
                    className="h-10 rounded-md border border-[var(--border-strong)] px-4 text-sm disabled:opacity-60"
                  >
                    {busy === "test" ? t("testing") : t("test")}
                  </button>
                  <button
                    type="button"
                    onClick={() => void remove()}
                    disabled={busy !== null}
                    className="h-10 rounded-md border border-[var(--border-strong)] px-4 text-sm disabled:opacity-60"
                  >
                    {t("remove")}
                  </button>
                </>
              )}
            </div>
            <div aria-live="polite">{message && <p className="text-sm">{message}</p>}</div>
            {error && (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {error}
              </p>
            )}
          </form>
          {webhook && (
            <div className="mt-4 text-sm">
              <p>{t("webhookUrl")}</p>
              <code className="block break-all" data-testid="bunny-webhook-url">
                {webhook}
              </code>
              <button
                type="button"
                onClick={() => void copy(webhook)}
                className="mt-2 rounded-md border border-[var(--border-strong)] px-3 py-1 text-sm"
              >
                {t("copy")}
              </button>
              <p aria-live="polite" className="mt-1 text-xs">
                {copyState === "copied"
                  ? t("copied")
                  : copyState === "failed"
                    ? t("copyFailed")
                    : ""}
              </p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * MT-1 Plan 4 S12 (FR-10, FR-37 UI): the platform admin sets a school's Bunny Stream library and
 * Bunny Storage zone. Secrets are write-only: inputs start empty, a blank one keeps the saved
 * value (only typed fields are sent), and the saved key's hint is shown beside it.
 */
export function SchoolBunnyConfig({ tenantId }: { tenantId: string }) {
  const t = useTranslations("platform.bunnyConfig");
  return (
    <section
      className="mt-6 rounded-lg border border-[var(--border-strong)] p-4"
      aria-labelledby="school-bunny-title"
      data-testid="school-bunny-config"
    >
      <h2 id="school-bunny-title" className="text-lg font-semibold">
        {t("title")}
      </h2>
      <p className="mt-1 text-sm text-[var(--text-muted)]">{t("intro")}</p>
      <BunnyForm tenantId={tenantId} kind="stream" />
      <BunnyForm tenantId={tenantId} kind="storage" />
    </section>
  );
}
